/**
 * Main App Component
 * Root component with routing - refactored for best practices
 */

import { Suspense, ReactElement } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { 
  PATHS, 
  PrivateRoute, 
  PublicRoute, 
  RouteWrapper,
  getAllRoutes,
  type RouteConfig
} from './routes';
import { PageLoader } from './components/common/Loading';
import { AuthInitializer } from './components/auth/AuthInitializer';

/**
 * Loading fallback component for lazy-loaded routes
 */
const LoadingFallback = (): ReactElement => <PageLoader tip="Loading page..." />;

/**
 * Renders a single route with appropriate guard and wrapper
 * Eliminates code duplication by handling all route types uniformly
 */
const renderRoute = (route: RouteConfig): ReactElement => {
  const Component = route.element;
  const content = (
    <RouteWrapper meta={route.meta}>
      <Component />
    </RouteWrapper>
  );

  // Apply appropriate guard based on route type
  let guardedContent: ReactElement;
  
  switch (route.guardType) {
    case 'public':
      guardedContent = (
        <PublicRoute restricted={route.restricted}>
          {content}
        </PublicRoute>
      );
      break;
    
    case 'protected':
    case 'admin':
      guardedContent = (
        <PrivateRoute meta={route.meta}>
          {content}
        </PrivateRoute>
      );
      break;
    
    default:
      guardedContent = content;
  }

  return (
    <Route
      key={route.path}
      path={route.path}
      element={guardedContent}
    />
  );
};

/**
 * Main application component
 */
function App(): ReactElement {
  const allRoutes = getAllRoutes();

  return (
    <div className="app">
      <AuthInitializer>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Render all routes using helper function */}
            {allRoutes.map(renderRoute)}

            {/* Catch all - 404 */}
            <Route path="*" element={<Navigate to={PATHS.NOT_FOUND} replace />} />
          </Routes>
        </Suspense>
      </AuthInitializer>
    </div>
  );
}

export default App;

